import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import {
  ScrollView,
  Text,
  View,
  Image,
  TouchableOpacity,
  TouchableWithoutFeedback,
  ActivityIndicator,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { useNavigation } from "@react-navigation/native";
import { toast } from "react-toastify";
import { navigateToScreen } from "../../../../utility/NavigationHelper";
import { AppScreens } from "../../../Utility/AppScreens";
import InviteSocialMediaModal from "../../Event/InviteSocialMediaModal";
import StreamDescription from "../../../../Components/ongoing-common-components/general-components/StreamDescription";
import RequiredAttendeesModal from "../../Event/RequiredAttendeesModal";
import Skeletons from "../../../../Components/skeleton-components/Skeletons";
import ImageComponent from "../../../../Components/ImageComponent";
import Separator from "../../../../Components/Separator";
import EventDetailCard from "./EventDetailCard";
import HowWorksTooltip from "./HowWorksTooltip";
import { useChat } from "../../../../context/ChatContext";
import { fetchUserStatus } from "../../../../services/chatAPI";
import { useNotifications } from "../../../../context/NotificationContext";
import ParticipantDetails from "./ParticipantDetails";
import { AppFontStyle } from "../../../../styles/AppFontStyle";
import { AppColors } from "../../../../utility/AppColors";
import { AppImages } from "../../../../utility/AppImages";
import { useEventTimer } from "./hooks/useEventTimer";
import { useCheckIn } from "./hooks/useCheckIn";
import { EventDetails, UserData } from "./types/event.types";
import {
  getFormattedTime,
  getCheckInFormattedTime,
  safeAsync,
} from "./utils/eventUtils";
import { format, isToday } from "date-fns";
import TimerComponent from "../../../../Components/ongoing-common-components/general-components/TimerComponent";

interface OnGoingGameShowGeneralProps {
  showLoader?: boolean;
  updateShowLoader?: boolean;
  details: EventDetails | null;
  description?: string;
  sponsor?: string;
  userData?: UserData | null;
  setSponsor?: (text: string) => void;
  setDescription?: (text: string) => void;
  callUpdateEventAPI?: () => Promise<void | boolean>;
  isOrganiserId?: boolean;
  setSelectedButton?: (text: string) => void;
  IsEventCanceled: boolean;
  IsEventCompleted?: boolean;
  hasCheckedIn?: boolean;
  setHasCheckedIn?: (value: boolean) => void;
  setShowLoader?: (value: boolean) => void;
  silentRefresh?: () => void;
  setShowPopup?: (value: boolean) => void;
  twitchStreamCreated?: boolean;
  youtubeStreamCreated?: boolean;
  isNativeAppConnected?: boolean;
  isPastEvent?: boolean;
}

const OnGoingGameShowGeneral: React.FC<OnGoingGameShowGeneralProps> = memo(
  ({
    showLoader,
    details,
    description = "",
    userData,
    setDescription,
    updateShowLoader,
    callUpdateEventAPI,
    isOrganiserId = false,
    setSelectedButton,
    IsEventCanceled,
    IsEventCompleted,
    hasCheckedIn,
    setHasCheckedIn,
    silentRefresh,
    setShowPopup,
    twitchStreamCreated = false,
    youtubeStreamCreated = false,
    isNativeAppConnected = false,
    isPastEvent = false,
  }) => {
    const styles = useStyles();
    const deviceSize = useDeviceSize();
    const navigation = useNavigation();
    const { checkInStatuses } = useChat();
    const { checkInNotifications } = useNotifications();

    const { timeLeft, checkInTimeLeft, isCheckInEnabled, isLessThanOneHour } =
      useEventTimer(details?.eventStartTime);

    const handleCheckInSuccess = useCallback((updatedDetails: Partial<EventDetails>) => {
      if (updatedDetails.checkInStatus) {
        setTempCheckedIn(true);

        if (silentRefresh) {
          setTimeout(() => {
            silentRefresh();
          }, 500);
        }
      }
    }, [isOrganiserId, silentRefresh]);

    const {
      loading: checkInLoading,
      hasCheckedIn: checkInStatus,
      handleCheckIn,
      canUserCheckIn,
    } = useCheckIn(
      userData,
      details,
      isOrganiserId || false,
      handleCheckInSuccess
    );
    const [isShare, setIsShare] = useState(false);
    const [requiredAttendees, setRequiredAttendees] = useState(false);
    const [saveBtnTooltip, setSaveBtnTooltip] = useState(false);
    const [isMoreTimeLeft, setIsMoreTimeLeft] = useState(false);
    const [isMinAttendees, setIsMinAttendees] = useState(false);
    const [userStatus, setUserStatus] = useState<{
      userID: string;
      status: string;
    } | null>(null);
    const [howWorksTooltip, setHowWorksTooltip] = useState(false);
    const [moreAboutEventExpanded, setMoreAboutEventExpanded] = useState(false);

    const [tempCheckedIn, setTempCheckedIn] = useState(false);

    useEffect(() => {
      if (details?.checkInStatus) {
        const isCheckedIn = isOrganiserId
          ? details.checkInStatus.organiser
          : details.checkInStatus.challenger;

        if (isCheckedIn && tempCheckedIn) {
          setTempCheckedIn(false);
        }
      }
    }, [details?.checkInStatus, isOrganiserId, tempCheckedIn]);
    const attendeeInfo = useMemo(() => {
      const signUpNumber = 70;
      const requiredNumber = details?.attendeeCount
        ? Math.max(details.attendeeCount - signUpNumber, 0)
        : 0;
      return { signUpNumber, requiredNumber };
    }, [details?.attendeeCount]);

    const finalCheckInStatus = useMemo(() => {
      if (tempCheckedIn) {
        return true;
      }

      if (details?.checkInStatus) {
        const currentUserCheckedIn = isOrganiserId
          ? details.checkInStatus.organiser
          : details.checkInStatus.challenger;
        return !!currentUserCheckedIn;
      }

      return checkInStatus || false;
    }, [tempCheckedIn, details?.checkInStatus, checkInStatus, isOrganiserId]);
    useEffect(() => {
      if (setHasCheckedIn) {
        setHasCheckedIn(finalCheckInStatus);
      }
    }, [finalCheckInStatus, setHasCheckedIn]);

    const fetchUserStatusData = useCallback(async () => {
      if (!userData?.AccessToken || !details?.generalChatConversationId) {
        return;
      }

      try {
        const response = await safeAsync(() =>
          fetchUserStatus(userData.AccessToken)
        );

        if (response?.statusCode === 200) {
          const conversation = response.body.conversations.find(
            (conv: any) =>
              conv.conversationId === details.generalChatConversationId
          );

          if (conversation && Object.keys(conversation.members).length > 0) {
            const uniqueMembers = Object.entries(conversation.members).reduce(
              (acc, [userID, status]) => {
                if (!acc[userID]) {
                  acc[userID] = status as string;
                }
                return acc;
              },
              {} as { [key: string]: string }
            );

            const firstUserID = Object.keys(uniqueMembers)[0];
            setUserStatus({
              userID: firstUserID,
              status: uniqueMembers[firstUserID],
            });
          } else {
            setUserStatus(null);
          }
        } else {
          setUserStatus(null);
        }
      } catch (error) {
        setUserStatus(null);
      }
    }, [userData?.AccessToken, details?.generalChatConversationId]);

    useEffect(() => {
      fetchUserStatusData();
    }, [fetchUserStatusData]);

    const handleOutsideClick = useCallback(() => {
      if (saveBtnTooltip) {
        setSaveBtnTooltip(false);
      }
      if (moreAboutEventExpanded) {
        setMoreAboutEventExpanded(false);
      }
    }, [saveBtnTooltip, moreAboutEventExpanded]);

    useEffect(() => {
      if (checkInNotifications.length > 0) {
        const latestNotification =
          checkInNotifications[checkInNotifications.length - 1];

        if (latestNotification.eventId === details?.eventID) {
          if (silentRefresh) {
            silentRefresh();
          }
        }
      }
    }, [
      checkInNotifications,
      details?.eventID,
      silentRefresh,
    ]);

    useEffect(() => {
      if (details?.generalChatConversationId && checkInStatuses?.userID) {
        const { userID, status } = checkInStatuses;

        setUserStatus((prev) =>
          prev && prev.userID === userID
            ? { ...prev, status }
            : { userID, status }
        );
      }
    }, [checkInStatuses, details?.generalChatConversationId]);

    const IsUserOnline = useMemo(() => {
      return userStatus?.status === "online";
    }, [userStatus?.status]);

    const currentDate = useMemo(() => new Date(), []);

    const isMobileView = (deviceSize as string) !== DEVICE_SIZES.XL;

    const formattedDate = useMemo(() => {
      return details?.eventStartTime && new Date(details.eventStartTime).toString() !== "Invalid Date"
        ? format(new Date(details.eventStartTime), "EEEE, MMMM d, yyyy, 'at' h:mm")
        : "";
    }, [details?.eventStartTime]);

    return (
      <TouchableWithoutFeedback onPress={handleOutsideClick}>
        <ScrollView style={{}} showsHorizontalScrollIndicator={false}>
          {/* Mobile View - Match Time and Check-in Cards */}
          {isMobileView && !showLoader && (
            <View style={{ flexDirection: "row", gap: 12, marginTop: 8, alignItems: "stretch" }}>
              {/* Match Time Box */}
              <View
                style={{
                  flex: 1,
                  height: 60,
                  paddingVertical: 7.757,
                  paddingHorizontal: 9.309,
                  borderRadius: 9.309,
                  backgroundColor: "#282828",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  <Image source={AppImages.MatchTime} style={{ width: 12, height: 12 }} />
                  <Text style={[AppFontStyle.BOLD_ANYBODY_12, { color: AppColors.gray_BBB }]}>Match Time</Text>
                </View>
                <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: AppColors.white, textAlign: "left", marginTop: 0 }]}>
                  {details?.eventStartTime
                    ? getFormattedTime(details.eventStartTime)
                    : "N/A"}
                </Text>
                <Text style={[AppFontStyle.REGULAR_ANYBODY_12, { color: AppColors.gray_BBB, textAlign: "left", marginTop: 0 }]}>
                  {details?.eventStartTime
                    ? format(new Date(details.eventStartTime), "MMMM d, yyyy")
                    : "N/A"}
                </Text>
              </View>

              {/* Check-in Opens Box */}
              <View
                style={{
                  flex: 1,
                  height: 60,
                  paddingVertical: 7.757,
                  paddingHorizontal: 9.309,
                  borderRadius: 9.309,
                  backgroundColor: "#282828",
                  borderWidth: 0.776,
                  borderColor: "#FACC15",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  <Image source={AppImages.CheckInTime} style={{ width: 12, height: 12 }} />
                  <Text style={[AppFontStyle.BOLD_ANYBODY_12, { color: AppColors.gray_BBB }]}>Check-in Opens</Text>
                </View>
                <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: "#FACC15", textAlign: "left", marginTop: 0 }]}>
                  {details?.eventStartTime
                    ? getCheckInFormattedTime(details.eventStartTime)
                    : "N/A"}
                </Text>
                {details?.eventStartTime && isToday(new Date(details.eventStartTime)) && (
                  <Text style={[AppFontStyle.REGULAR_ANYBODY_12, { color: AppColors.gray_BBB, textAlign: "left", marginTop: 0 }]}>
                    90 minutes before match
                  </Text>
                )}
              </View>
            </View>
          )}

          {/* Mobile View - Timer Section Outside */}
          {isMobileView && (
            <LinearGradient
              colors={["#1F2937", "#111827"]}
              start={[0, 0]}
              end={[0, 1]}
              style={{
                marginTop: 8,
                paddingVertical: 8,
                paddingHorizontal: 8,
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                alignItems: "center",
                marginBottom: 5,
              }}
            >
              {showLoader ? (
                <Skeletons height={150} width={"100%"} style={{ borderRadius: 8 }} />
              ) : (
                <TimerComponent
                  isMoreTimeLeft={isMoreTimeLeft}
                  isMinAttendees={isMinAttendees}
                  timeLeft={timeLeft}
                  requiredNumber={attendeeInfo.requiredNumber}
                  totalPercentage={0}
                  showTimer={false}
                  formattedDate=""
                  title={details?.eventTitle || ""}
                  type="EventDetail"
                  IsEventCanceled={IsEventCanceled}
                  IsEventCompleted={IsEventCompleted}
                />
              )}
            </LinearGradient>
          )}



          {/* Mobile View - Collapsible More About Event */}
          {isMobileView && (
            <LinearGradient
              colors={["#1F2937", "#111827"]}
              start={[0, 0]}
              end={[0, 1]}
              style={{
                marginTop: 8,
                borderRadius: 8,
                borderWidth: 0.8,
                borderColor: "#374151",
                overflow: "hidden",
              }}
            >
              {/* Dropdown Button */}
              <TouchableOpacity
                onPress={() => setMoreAboutEventExpanded(!moreAboutEventExpanded)}
                style={{
                  paddingVertical: 8,
                  paddingHorizontal: 6,
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Text style={[AppFontStyle.BOLD_ANYBODY_14, { color: AppColors.white }]}>
                  More about event
                </Text>
                <Image
                  source={moreAboutEventExpanded ? AppImages.arrowUp : AppImages.arrowDown}
                  style={{ width: 20, height: 20 }}
                  resizeMode="contain"
                />
              </TouchableOpacity>

              {/* Expandable Content - No Gap */}
              {moreAboutEventExpanded && (
                <View>
                  <EventDetailCard
                    screenName={""}
                    setIsMinAttendees={setIsMinAttendees}
                    isMinAttendees={isMinAttendees}
                    details={details}
                    saveBtnTooltip={saveBtnTooltip}
                    setSaveBtnTooltip={setSaveBtnTooltip}
                    userData={userData}
                    timeLeft={timeLeft}
                    setTimeLeft={() => { }}
                    hasCheckedIn={finalCheckInStatus}
                    setSelectedButton={setSelectedButton}
                    isMoreTimeLeft={isMoreTimeLeft}
                    setIsMoreTimeLeft={setIsMoreTimeLeft}
                    IsEventCanceled={IsEventCanceled}
                    IsEventCompleted={IsEventCompleted}
                    isPastEvent={isPastEvent}
                    isLoading={showLoader}
                    hideTimer={true}
                  />
                </View>
              )}
            </LinearGradient>
          )}

          {/* Desktop View - Always Visible */}
          {!isMobileView && (
            <EventDetailCard
              screenName={""}
              setIsMinAttendees={setIsMinAttendees}
              isMinAttendees={isMinAttendees}
              details={details}
              saveBtnTooltip={saveBtnTooltip}
              setSaveBtnTooltip={setSaveBtnTooltip}
              userData={userData}
              timeLeft={timeLeft}
              setTimeLeft={() => { }}
              hasCheckedIn={finalCheckInStatus}
              setSelectedButton={setSelectedButton}
              isMoreTimeLeft={isMoreTimeLeft}
              setIsMoreTimeLeft={setIsMoreTimeLeft}
              IsEventCanceled={IsEventCanceled}
              IsEventCompleted={IsEventCompleted}
              isLoading={showLoader}
              isPastEvent={isPastEvent}
            />
          )}
          {/* Mobile Layout: Check-in first, then User Details and Description */}
          {isMobileView ? (
            <View style={{ marginTop: 8, gap: 16, zIndex: -1 }}>
              <LinearGradient
                colors={["rgba(127, 29, 29, 0.20)", "rgba(153, 27, 27, 0.20)"]}
                start={[0, 0]}
                end={[1, 1]}
                style={{
                  padding: 12,
                  borderRadius: 8,
                  borderWidth: 0.8,
                  borderColor: "#EF4444",
                  flex: 1,
                  shadowColor: "rgba(255, 255, 255, 0.15)",
                  shadowOffset: { width: 0, height: 0 },
                  shadowOpacity: 1,
                  shadowRadius: 19,
                }}
              >
                {showLoader ? (
                  <Skeletons
                    height={"160px"}
                    style={{
                      borderRadius: 12,
                    }}
                  />
                ) : (
                  <>
                    <View
                      style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Text style={AppFontStyle.BOLD_ANYBODY_18}>Check-in</Text>
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <Text style={AppFontStyle.MEDIUM_ANYBODY_14}>
                          How does it work?
                        </Text>
                        <TouchableOpacity
                          onPress={() => setHowWorksTooltip(true)}
                        >
                          <Image
                            source={AppImages.icQuestionInfo}
                            style={{ height: 20, width: 20 }}
                          />
                        </TouchableOpacity>
                      </View>
                    </View>
                    <Separator
                      color={AppColors.white}
                      height={1}
                      marginTop={12}
                      marginBottom={6}
                      marginHorizontal={0}
                    />
                    <View style={{ paddingVertical: isMobileView ? 0 : 16 }}>
                      {!finalCheckInStatus && !isMobileView && (
                        <View>
                          <View
                            style={{
                              flexDirection: "row",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Text style={AppFontStyle.REGULAR_ANYBODY_14}>
                              {(() => {
                                const eventStartTime = new Date(
                                  details?.eventStartTime || ""
                                );
                                if (
                                  currentDate > eventStartTime &&
                                  !finalCheckInStatus
                                )
                                  return "Check In Closed";
                                return isCheckInEnabled
                                  ? "Check in is open"
                                  : "Check in opens at:";
                              })()}
                            </Text>
                            {!isCheckInEnabled &&
                              details?.eventStartTime &&
                              currentDate <= new Date(details.eventStartTime) && (
                                <Text
                                  style={[
                                    AppFontStyle.BOLD_ANYBODY_16,
                                    {
                                      color: AppColors.white,
                                      textAlign: "right",
                                    },
                                  ]}
                                >
                                  {getCheckInFormattedTime(
                                    details.eventStartTime
                                  )}
                                </Text>
                              )}
                          </View>
                          {isCheckInEnabled &&
                            details?.eventStartTime &&
                            currentDate <= new Date(details.eventStartTime) && (
                              <Text
                                style={{
                                  ...AppFontStyle.REGULAR_ANYBODY_14,
                                  color: AppColors.gray_9CA3AF,
                                  marginTop: 4,
                                }}
                              >
                                Click check in to confirm your participation
                              </Text>
                            )}
                          {details?.eventStartTime &&
                            currentDate > new Date(details.eventStartTime) &&
                            !finalCheckInStatus && (
                              <Text
                                style={{
                                  ...AppFontStyle.REGULAR_ANYBODY_14,
                                  color: AppColors.red_CF366E,
                                  marginTop: 6,
                                }}
                              >
                                You have missed the Check In
                              </Text>
                            )}
                        </View>
                      )}
                      {finalCheckInStatus && (
                        <View>
                          <Text style={AppFontStyle.REGULAR_ANYBODY_14}>
                            Checked in
                          </Text>
                          <View
                            style={{
                              backgroundColor: AppColors.green_14532D,
                              paddingVertical: 12,
                              borderRadius: 10,
                              marginTop: 12,
                              flexDirection: "row",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderColor: AppColors.green_16A34A,
                              borderWidth: 2,
                              paddingHorizontal: 12,
                            }}
                          >
                            <View
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                              }}
                            >
                              <Image
                                source={AppImages.CheckInTick}
                                style={{ width: 20, height: 20, marginRight: 8 }}
                              />
                            </View>
                            <View style={{ flex: 1, paddingHorizontal: 8 }}>
                              <Text style={AppFontStyle.REGULAR_ANYBODY_14}>
                                Successfully Checked In
                              </Text>
                              <Text
                                style={[
                                  AppFontStyle.REGULAR_ANYBODY_12,
                                  { color: AppColors.green_4ADE80 },
                                ]}
                              >
                                You can now set up your stream
                              </Text>
                            </View>
                            <View
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                              }}
                            >
                              <Image
                                source={AppImages.CheckInReady}
                                style={{ width: 20, height: 20, marginRight: 8 }}
                              />
                              <Text
                                style={[
                                  AppFontStyle.REGULAR_ANYBODY_14,
                                  { color: AppColors.green_4ADE80 },
                                ]}
                              >
                                Ready
                              </Text>
                            </View>
                          </View>
                        </View>
                      )}
                    </View>
                    {!isMobileView && (
                      <Separator
                        color={AppColors.white}
                        height={1}
                        marginTop={8}
                        marginBottom={6}
                        marginHorizontal={0}
                      />
                    )}
                    {!finalCheckInStatus && (
                      <TouchableOpacity
                        style={{
                          width: "100%",
                          backgroundColor: IsEventCanceled
                            ? AppColors.gray_353535
                            : checkInLoading
                              ? AppColors.green_14532D
                              : canUserCheckIn
                                ? '#16A34A'  // Brighter green when enabled
                                : AppColors.green_15803D,
                          paddingVertical: 12,
                          borderRadius: 10,
                          marginTop: 14,
                          marginBottom: 6,
                          borderWidth: IsEventCanceled ? 2 : 0,
                          borderColor: IsEventCanceled
                            ? AppColors.red_CF366E
                            : "transparent",
                          opacity:
                            !canUserCheckIn ||
                              checkInLoading ||
                              IsEventCanceled ||
                              IsEventCompleted
                              ? 0.7
                              : 1,
                          flexDirection: "row",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                        disabled={
                          !canUserCheckIn ||
                          checkInLoading ||
                          IsEventCanceled ||
                          IsEventCompleted
                        }
                        onPress={handleCheckIn}
                      >
                        {checkInLoading && (
                          <ActivityIndicator
                            size="small"
                            color={AppColors.white}
                            style={{ marginRight: 8 }}
                          />
                        )}
                        <Text
                          style={[
                            AppFontStyle.BOLD_ANYBODY_12,
                            {
                              textAlign: "center",
                              color: IsEventCanceled
                                ? AppColors.red_CF366E
                                : AppColors.white,
                            },
                          ]}
                        >
                          {checkInLoading
                            ? "Checking In..."
                            : IsEventCanceled
                              ? details?.cancelReason || "Event Cancelled"
                              : IsEventCompleted
                                ? "Event Completed"
                                : canUserCheckIn
                                  ? "Check in Now"
                                  : "Check in"}
                        </Text>
                      </TouchableOpacity>
                    )}
                    {/* Conditional Button: Enter Stream Room or Set Up My Stream */}
                    {(twitchStreamCreated || youtubeStreamCreated) && isNativeAppConnected ? (
                      // Show "Enter Stream Room" button when streams are created AND KO Broadcaster is connected
                      <TouchableOpacity
                        style={{
                          width: "100%",
                          backgroundColor: finalCheckInStatus && !IsEventCanceled && !IsEventCompleted
                            ? AppColors.appViolentColor_6D08C3
                            : AppColors.gray_6D6D6D,
                          paddingVertical: 12,
                          borderRadius: 10,
                          marginTop: finalCheckInStatus ? 14 : 6,
                          marginBottom: 6,
                          opacity:
                            !finalCheckInStatus ||
                              IsEventCanceled ||
                              IsEventCompleted
                              ? 0.6
                              : 1,
                        }}
                        disabled={
                          !finalCheckInStatus ||
                          IsEventCanceled ||
                          IsEventCompleted
                        }
                        onPress={() => {
                          if (!finalCheckInStatus) {
                            toast.error("Please check in first to enter the stream room");
                            return;
                          }
                          if (!details?.eventID || !userData?.AccessToken) {
                            toast.error("Missing event or auth details");
                            return;
                          }
                          // Navigate directly to stream setup
                          navigateToScreen(navigation, AppScreens.StreamSetup, {
                            eventId: details.eventID,
                          });
                        }}
                      >
                        <View
                          style={{
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Text style={{ fontSize: 16, marginRight: 8 }}>🚀</Text>
                          <Text
                            style={[
                              AppFontStyle.BOLD_ANYBODY_12,
                              { textAlign: "center", color: AppColors.white },
                            ]}
                          >
                            {!finalCheckInStatus
                              ? "Check-in Required"
                              : "Enter Stream Room"}
                          </Text>
                        </View>
                      </TouchableOpacity>
                    ) : (
                      <TouchableOpacity
                        style={{
                          width: "100%",
                          backgroundColor: "#DC2626",
                          paddingVertical: 12,
                          borderRadius: 10,
                          marginTop: finalCheckInStatus ? 14 : 6,
                          marginBottom: 6,
                          opacity: IsEventCanceled || IsEventCompleted ? 0.6 : 1,
                        }}
                        disabled={IsEventCanceled || IsEventCompleted}
                        onPress={() => {
                          if (setSelectedButton) {
                            setSelectedButton("Set-up stream");
                          }
                        }}
                      >
                        <View
                          style={{
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <View
                            style={{
                              width: 20,
                              height: 20,
                              marginRight: 8,
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Text style={{ fontSize: 16 }}>🎥</Text>
                          </View>
                          <Text
                            style={[
                              AppFontStyle.BOLD_ANYBODY_12,
                              { textAlign: "center", color: AppColors.white },
                            ]}
                          >
                            Set Up My Stream
                          </Text>
                        </View>
                      </TouchableOpacity>
                    )}
                  </>
                )}
                {howWorksTooltip && (
                  <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
                    <View
                      style={[
                        {
                          right:
                            deviceSize === DEVICE_SIZES.XS ||
                              deviceSize === DEVICE_SIZES.SM
                              ? 0
                              : 30,
                          position: "absolute",
                          top: -10,
                          zIndex: 9999,
                        },
                      ]}
                    >
                      <HowWorksTooltip
                        bottom={
                          deviceSize === DEVICE_SIZES.XS ||
                            deviceSize === DEVICE_SIZES.SM
                            ? -215
                            : -40
                        }
                        right={
                          deviceSize === DEVICE_SIZES.XS ||
                            deviceSize === DEVICE_SIZES.SM
                            ? 0
                            : 20
                        }
                        width={
                          deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                            ? 557
                            : 336
                        }
                        visible={howWorksTooltip}
                        setVisible={setHowWorksTooltip}
                      />
                    </View>
                  </TouchableWithoutFeedback>
                )}
              </LinearGradient>

              {/* User Data Box - Mobile */}
              <LinearGradient
                colors={["#1F2937", "#111827"]}
                start={[0, 0]}
                end={[0, 1]}
                style={{
                  padding: 8,
                  paddingHorizontal: 12,
                  borderRadius: 8,
                  borderWidth: 0.8,
                  borderColor: "#374151",
                  gap: 18,
                }}
              >
                {showLoader ? (
                  <Skeletons
                    height={60}
                    width={"100%"}
                    style={{ borderRadius: 10 }}
                  />
                ) : (
                  <ParticipantDetails
                    participantDetails={details?.participantDetails || []}
                    checkInStatus={
                      details?.checkInStatus || {
                        organiser: false,
                        challenger: false,
                      }
                    }
                    eventStartTime={details?.eventStartTime || ""}
                    currentUserId={userData?.username || ""}
                    isOrganiserId={isOrganiserId || false}
                    isUserOnline={IsUserOnline}
                  />
                )}
              </LinearGradient>

              {/* Stream Description Box - Mobile */}
              <LinearGradient
                colors={["#1F2937", "#111827"]}
                start={[0, 0]}
                end={[0, 1]}
                style={{
                  borderRadius: 8,
                  padding: 8,
                  paddingHorizontal: 12,
                  gap: 18,
                  marginBottom: 20,
                }}
              >
                {showLoader ? (
                  <>
                    <Skeletons
                      height={30}
                      width={"200px"}
                      style={{ borderRadius: 10, marginBottom: 10 }}
                    />
                    <Skeletons height={60} style={{ borderRadius: 10 }} />
                  </>
                ) : (
                  <StreamDescription
                    title="Stream description"
                    description={description}
                    editable={false}
                    setDescription={setDescription}
                    numberOfLines={3}
                  />
                )}
              </LinearGradient>
            </View>
          ) : (
            // Desktop Layout
            <View
              style={[
                styles.deviceflexDirection,
                {
                  marginVertical: 30,
                  gap: 20,
                  marginHorizontal: 0,
                  zIndex: -1,
                },
              ]}
            >
              {/* Left Column - 50% - User Data + Description */}
              <View style={{ flexDirection: "column", flex: 1 }}>
                {/* User Data Box */}
                <LinearGradient
                  colors={["#1F2937", "#111827"]}
                  start={[0, 0]}
                  end={[0, 1]}
                  style={{
                    padding: 8,
                    paddingHorizontal: 12,
                    borderRadius: 8,
                    borderWidth: 0.8,
                    borderColor: "#374151",
                    gap: 18,
                    marginBottom: 16,
                  }}
                >
                  {showLoader ? (
                    <Skeletons
                      height={60}
                      width={"100%"}
                      style={{ borderRadius: 10 }}
                    />
                  ) : (
                    <ParticipantDetails
                      participantDetails={details?.participantDetails || []}
                      checkInStatus={
                        details?.checkInStatus || {
                          organiser: false,
                          challenger: false,
                        }
                      }
                      eventStartTime={details?.eventStartTime || ""}
                      currentUserId={userData?.username || ""}
                      isOrganiserId={isOrganiserId || false}
                      isUserOnline={IsUserOnline}
                    />
                  )}
                </LinearGradient>

                {/* Stream Description Box */}
                <LinearGradient
                  colors={["#1F2937", "#111827"]}
                  start={[0, 0]}
                  end={[0, 1]}
                  style={{
                    borderRadius: 8,
                    flex: 1,
                    padding: 8,
                    paddingHorizontal: 12,
                    gap: 18,
                  }}
                >
                  {showLoader ? (
                    <>
                      <Skeletons
                        height={30}
                        width={"200px"}
                        style={{ borderRadius: 10, marginBottom: 10 }}
                      />
                      <Skeletons height={60} style={{ borderRadius: 10 }} />
                    </>
                  ) : (
                    <StreamDescription
                      title="Stream description"
                      description={description}
                      editable={false}
                      setDescription={setDescription}
                      numberOfLines={3}
                    />
                  )}
                </LinearGradient>
              </View>
              {/* Right Column - 50% - Check-in Box */}
              <LinearGradient
                colors={["rgba(127, 29, 29, 0.20)", "rgba(153, 27, 27, 0.20)"]}
                start={[0, 0]}
                end={[1, 1]}
                style={{
                  padding: 12,
                  borderRadius: 8,
                  borderWidth: 0.8,
                  borderColor: "#EF4444",
                  flex: 1,
                  shadowColor: "rgba(255, 255, 255, 0.15)",
                  shadowOffset: { width: 0, height: 0 },
                  shadowOpacity: 1,
                  shadowRadius: 19,
                }}
              >
                {showLoader ? (
                  <Skeletons
                    height={"230px"}
                    style={{
                      borderRadius: 12,
                    }}
                  />
                ) : (
                  <>
                    <View
                      style={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Text style={AppFontStyle.BOLD_ANYBODY_18}>Check-in</Text>
                      <View
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <Text style={AppFontStyle.MEDIUM_ANYBODY_14}>
                          How does it work?
                        </Text>
                        <TouchableOpacity
                          onPress={() => setHowWorksTooltip(true)}
                        >
                          <Image
                            source={AppImages.icQuestionInfo}
                            style={{ height: 20, width: 20 }}
                          />
                        </TouchableOpacity>
                      </View>
                    </View>
                    <Separator
                      color={AppColors.white}
                      height={1}
                      marginTop={18}
                      marginBottom={6}
                      marginHorizontal={0}
                    />
                    <View style={{ paddingVertical: 16 }}>
                      {!finalCheckInStatus && !isMobileView && (
                        <View>
                          <View
                            style={{
                              flexDirection: "row",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Text style={AppFontStyle.REGULAR_ANYBODY_14}>
                              {(() => {
                                const eventStartTime = new Date(
                                  details?.eventStartTime || ""
                                );
                                if (
                                  currentDate > eventStartTime &&
                                  !finalCheckInStatus
                                )
                                  return "Check In Closed";
                                return isCheckInEnabled
                                  ? "Check in is open"
                                  : "Check in opens at:";
                              })()}
                            </Text>
                            {!isCheckInEnabled &&
                              details?.eventStartTime &&
                              currentDate <= new Date(details.eventStartTime) && (
                                <Text
                                  style={[
                                    AppFontStyle.BOLD_ANYBODY_16,
                                    {
                                      color: AppColors.white,
                                      textAlign: "right",
                                    },
                                  ]}
                                >
                                  {getCheckInFormattedTime(
                                    details.eventStartTime
                                  )}
                                </Text>
                              )}
                          </View>
                          {isCheckInEnabled &&
                            details?.eventStartTime &&
                            currentDate <= new Date(details.eventStartTime) && (
                              <Text
                                style={{
                                  ...AppFontStyle.REGULAR_ANYBODY_14,
                                  color: AppColors.gray_9CA3AF,
                                  marginTop: 4,
                                }}
                              >
                                Click check in to confirm your participation
                              </Text>
                            )}
                          {details?.eventStartTime &&
                            currentDate > new Date(details.eventStartTime) &&
                            !finalCheckInStatus && (
                              <Text
                                style={{
                                  ...AppFontStyle.REGULAR_ANYBODY_14,
                                  color: AppColors.red_CF366E,
                                  marginTop: 6,
                                }}
                              >
                                You have missed the Check In
                              </Text>
                            )}
                        </View>
                      )}
                      {finalCheckInStatus && (
                        <View>
                          <Text style={AppFontStyle.REGULAR_ANYBODY_14}>
                            Checked in
                          </Text>
                          <View
                            style={{
                              backgroundColor: AppColors.green_14532D,
                              paddingVertical: 12,
                              borderRadius: 10,
                              marginTop: 12,
                              flexDirection: "row",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderColor: AppColors.green_16A34A,
                              borderWidth: 2,
                              paddingHorizontal: 12,
                            }}
                          >
                            <View
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                              }}
                            >
                              <Image
                                source={AppImages.CheckInTick}
                                style={{ width: 20, height: 20, marginRight: 8 }}
                              />
                            </View>
                            <View style={{ flex: 1, paddingHorizontal: 8 }}>
                              <Text style={AppFontStyle.REGULAR_ANYBODY_14}>
                                Successfully Checked In
                              </Text>
                              <Text
                                style={[
                                  AppFontStyle.REGULAR_ANYBODY_12,
                                  { color: AppColors.green_4ADE80 },
                                ]}
                              >
                                You can now set up your stream
                              </Text>
                            </View>
                            <View
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                              }}
                            >
                              <Image
                                source={AppImages.CheckInReady}
                                style={{ width: 20, height: 20, marginRight: 8 }}
                              />
                              <Text
                                style={[
                                  AppFontStyle.REGULAR_ANYBODY_14,
                                  { color: AppColors.green_4ADE80 },
                                ]}
                              >
                                Ready
                              </Text>
                            </View>
                          </View>
                        </View>
                      )}
                    </View>
                    <Separator
                      color={AppColors.white}
                      height={1}
                      marginTop={8}
                      marginBottom={6}
                      marginHorizontal={0}
                    />
                    {!finalCheckInStatus && (
                      <TouchableOpacity
                        style={{
                          width: '100%',
                          backgroundColor: IsEventCanceled
                            ? AppColors.gray_353535
                            : checkInLoading
                              ? AppColors.green_14532D
                              : canUserCheckIn
                                ? '#16A34A'
                                : AppColors.green_15803D,
                          paddingVertical: 12,
                          borderRadius: 10,
                          marginTop: 14,
                          marginBottom: 6,
                          borderWidth: IsEventCanceled ? 2 : 0,
                          borderColor: IsEventCanceled
                            ? AppColors.red_CF366E
                            : "transparent",
                          opacity:
                            !canUserCheckIn ||
                              checkInLoading ||
                              IsEventCanceled ||
                              IsEventCompleted
                              ? 0.7
                              : 1,
                          flexDirection: "row",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                        disabled={
                          !canUserCheckIn ||
                          checkInLoading ||
                          IsEventCanceled ||
                          IsEventCompleted
                        }
                        onPress={handleCheckIn}
                      >
                        {checkInLoading && (
                          <ActivityIndicator
                            size="small"
                            color={AppColors.white}
                            style={{ marginRight: 8 }}
                          />
                        )}
                        <Text
                          style={[
                            AppFontStyle.BOLD_ANYBODY_12,
                            {
                              textAlign: "center",
                              color: IsEventCanceled
                                ? AppColors.red_CF366E
                                : AppColors.white,
                            },
                          ]}
                        >
                          {checkInLoading
                            ? "Checking In..."
                            : IsEventCanceled
                              ? details?.cancelReason || "Event Cancelled"
                              : IsEventCompleted
                                ? "Event Completed"
                                : canUserCheckIn
                                  ? "Check in Now"
                                  : "Check in"}
                        </Text>
                      </TouchableOpacity>
                    )}
                    {(twitchStreamCreated || youtubeStreamCreated) && isNativeAppConnected ? (
                      <TouchableOpacity
                        style={{
                          width: '100%',
                          backgroundColor: finalCheckInStatus && !IsEventCanceled && !IsEventCompleted
                            ? AppColors.appViolentColor_6D08C3
                            : AppColors.gray_6D6D6D,
                          paddingVertical: 12,
                          borderRadius: 10,
                          marginTop: finalCheckInStatus ? 14 : 6,
                          marginBottom: 6,
                          opacity:
                            !finalCheckInStatus ||
                              IsEventCanceled ||
                              IsEventCompleted
                              ? 0.6
                              : 1,
                        }}
                        disabled={
                          !finalCheckInStatus ||
                          IsEventCanceled ||
                          IsEventCompleted
                        }
                        onPress={() => {
                          if (!finalCheckInStatus) {
                            toast.error("Please check in first to enter the stream room");
                            return;
                          }
                          if (!details?.eventID || !userData?.AccessToken) {
                            toast.error("Missing event or auth details");
                            return;
                          }
                          navigateToScreen(navigation, AppScreens.StreamSetup, {
                            eventId: details.eventID,
                          });
                        }}
                      >
                        <View
                          style={{
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <Text style={{ fontSize: 16, marginRight: 8 }}>🚀</Text>
                          <Text
                            style={[
                              AppFontStyle.BOLD_ANYBODY_12,
                              { textAlign: "center", color: AppColors.white },
                            ]}
                          >
                            {!finalCheckInStatus
                              ? "Check-in Required"
                              : "Enter Stream Room"}
                          </Text>
                        </View>
                      </TouchableOpacity>
                    ) : (
                      <TouchableOpacity
                        style={{
                          width: '100%',
                          backgroundColor: '#DC2626',
                          paddingVertical: 12,
                          borderRadius: 10,
                          marginTop: finalCheckInStatus ? 14 : 6,
                          marginBottom: 6,
                          opacity:
                            IsEventCanceled ||
                              IsEventCompleted
                              ? 0.6
                              : 1,
                        }}
                        disabled={
                          IsEventCanceled ||
                          IsEventCompleted
                        }
                        onPress={() => {
                          if (setSelectedButton) {
                            setSelectedButton("Set-up stream");
                          }
                          // Removed mobile popup - now shows stream setup content directly
                        }}
                      >
                        <View
                          style={{
                            flexDirection: "row",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          <View
                            style={{
                              width: 20,
                              height: 20,
                              marginRight: 8,
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <Text style={{ fontSize: 16 }}>🎥</Text>
                          </View>
                          <Text
                            style={[
                              AppFontStyle.BOLD_ANYBODY_12,
                              { textAlign: "center" },
                            ]}
                          >
                            Set Up My Stream
                          </Text>
                        </View>
                      </TouchableOpacity>
                    )}
                  </>
                )}
                {howWorksTooltip && (
                  <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
                    <View
                      style={[
                        {
                          right: 30,
                          position: "absolute",
                          top: -10,
                          zIndex: 9999,
                        },
                      ]}
                    >
                      <HowWorksTooltip
                        bottom={-40}
                        right={20}
                        width={557}
                        visible={howWorksTooltip}
                        setVisible={setHowWorksTooltip}
                      />
                    </View>
                  </TouchableWithoutFeedback>
                )}
              </LinearGradient>
            </View>
          )}
          {isShare && (
            <InviteSocialMediaModal
              accessToken={userData?.AccessToken || ""}
              challengeDetails={{}}
              showModal={isShare}
              setShowModal={setIsShare}
              eventType={"OnGoingGameShowGeneral"}
              eventID={details?.eventID || ""}
              status={details?.status || ""}
            />
          )}
          {requiredAttendees && (
            <RequiredAttendeesModal
              details={details}
              accessToken={userData?.AccessToken || ""}
              challengeDetails={{}}
              showModal={requiredAttendees}
              setShowModal={setRequiredAttendees}
              eventType={"OnGoingGameShowGeneral"}
            />
          )}
        </ScrollView>
      </TouchableWithoutFeedback>
    );
  }
);

OnGoingGameShowGeneral.displayName = "OnGoingGameShowGeneral";

export default OnGoingGameShowGeneral;

const useStyles = CreateResponsiveStyle(
  {
    countdownContainer: {
      backgroundColor: "transparent",
      borderRadius: 20,
      padding: 20,
    },
    addDescriptionView: {
      flex: 1,
    },
    icon: {
      width: 20,
      height: 20,
      marginBottom: 5,
    },
    readyIcon: {
      width: 8,
      height: 8,
      marginBottom: 5,
    },
    eventDetailsContainer: {
      alignSelf: "flex-end",
      marginVertical: 36,
      flex: 1,
    },
    gameshowOuterView: {
      backgroundColor: AppColors.grayColor_282828,
      borderRadius: 12,
      paddingHorizontal: 15,
    },
    btnStyle: {
      paddingVertical: 0,
      paddingHorizontal: 10,
      shadowOpacity: 0,
      height: 44,
      alignSelf: "center",
      margin: 10,
      borderRadius: 7,
      width: 212,
    },
    warnBtnStyle: {
      paddingVertical: 0,
      paddingHorizontal: 10,
      backgroundColor: AppColors.red_AF0707,
      shadowOpacity: 0,
      height: 44,
      alignSelf: "center",
      borderRadius: 7,
      bottom: 90,
      position: "absolute",
      right: 450,
      top: -177,
      width: 234,
      boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
    },
    optionBtnStyle: {
      paddingVertical: 0,
      paddingHorizontal: 10,
      backgroundColor: AppColors.appGreenColor_51AF07,
      shadowOpacity: 0,
      height: 44,
      alignSelf: "center",
      borderRadius: 7,
      bottom: 90,
      position: "absolute",
      right: 350,
      top: -177,
      width: 94,
      boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
    },
    allBtns: {
      position: "absolute",
      right: 0,
    },
    streamBtnView: {},
    deviceflexDirection: {
      flexDirection: "row",
    },
  },
  {
    [DEVICE_SIZES.MD]: {
      countdownContainer: {
        backgroundColor: "transparent",
        borderRadius: 20,
        padding: 20,
        marginBottom: 20,
      },
      addDescriptionView: {
        width: "100%",
      },
      eventDetailsContainer: {
        alignSelf: "flex-start",
      },
      gameshowOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
      btnStyle: {
        shadowOpacity: 0,
        height: 44,
        alignSelf: "flex-end",
        borderRadius: 7,
        width: "40%",
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      allBtns: {
        position: "static",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      },
      warnBtnStyle: {
        top: -60,
        right: 160,
      },
      optionBtnStyle: {
        top: -60,
        right: 470,
      },
    },
    [DEVICE_SIZES.LG]: {
      countdownContainer: {
        backgroundColor: "transparent",
        borderRadius: 20,
        padding: 20,
        marginBottom: 20,
      },
      addDescriptionView: {
        width: "100%",
      },
      eventDetailsContainer: {
        alignSelf: "center",
      },
      gameshowOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
      btnStyle: {
        shadowOpacity: 0,
        height: 44,
        alignSelf: "flex-end",
        borderRadius: 7,
        width: "40%",
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      allBtns: {
        position: "static",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      },
      warnBtnStyle: {
        top: -60,
        right: 160,
      },
      optionBtnStyle: {
        top: -60,
        right: 470,
      },
    },
    [DEVICE_SIZES.SM]: {
      countdownContainer: {
        backgroundColor: "transparent",
        borderRadius: 20,
        padding: 20,
        marginBottom: 20,
      },
      addDescriptionView: {
        width: "100%",
      },
      eventDetailsContainer: {
        borderRadius: 10,
        alignSelf: "flex-end",
        marginVertical: 15,
      },
      gameshowOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
      btnStyle: {
        shadowOpacity: 0,
        height: 44,
        alignSelf: "flex-end",
        bottom: 10,
        borderRadius: 7,
        width: "50%",
        position: "static",
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      allBtns: {
        position: "static",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      },
      warnBtnStyle: {
        right: 20,
        top: 0,
      },
      optionBtnStyle: {
        right: -80,
        top: 0,
      },
    },
    [DEVICE_SIZES.XS]: {
      countdownContainer: {
        backgroundColor: "transparent",
        borderRadius: 20,
        padding: 20,
        marginBottom: 20,
      },
      addDescriptionView: {
        width: "100%",
      },
      eventDetailsContainer: {
        alignSelf: "center",
        marginVertical: 15,
      },
      gameshowOuterView: {
        margin: 5,
        backgroundColor: AppColors.grayColor_282828,
        borderRadius: 12,
        paddingHorizontal: 10,
      },
      btnStyle: {
        paddingVertical: 0,
        shadowOpacity: 0,
        height: 44,
        alignSelf: "flex-end",
        bottom: 10,
        borderRadius: 7,
        width: "60%",
        position: "static",
      },
      deviceflexDirection: {
        flexDirection: "column",
      },
      allBtns: {
        position: "static",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
      },
      warnBtnStyle: {
        right: 20,
        top: 0,
      },
      optionBtnStyle: {
        right: -80,
        top: 0,
      },
    },
  }
);
